import crypto from 'crypto';
import config from '../../config';
import { CustomNextPageContext } from '../../../lib/customNextPageContext';

const COOKIE_NAME = 'tol-preview-access';

/**
 * Verifies the `tol-preview-access` cookie set by WordPress for
 * authenticated staff. Cookie shape: "<expiry>|<hmac-sha256(expiry, secret)>"
 */
export const hasValidPreviewAccess = (
  cookieHeaderValue: string | undefined,
): boolean => {
  if (!cookieHeaderValue) return false;

  const [expiryStr, signature] = cookieHeaderValue.split('|');
  if (!expiryStr || !signature) return false;

  const expiry = Number(expiryStr);
  if (!Number.isFinite(expiry) || expiry < Math.floor(Date.now() / 1000)) {
    return false; // expired or malformed
  }

  const expectedSignature = crypto
    .createHmac('sha256', config.previewAccessSecret)
    .update(expiryStr)
    .digest('hex');

  // constant-time comparison
  const a = Buffer.from(signature);
  const b = Buffer.from(expectedSignature);
  if (a.length !== b.length) return false;

  return crypto.timingSafeEqual(a, b);
};

/**
 * Reads a single cookie by name from a raw `document.cookie` string.
 */
const readCookieFromDocumentString = (
  documentCookie: string,
  name: string,
): string | undefined => {
  const match = documentCookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));

  if (!match) return undefined;

  return decodeURIComponent(match.substring(name.length + 1));
};

/**
 * Reads the preview-access cookie in both SSR and client-side-navigation
 * contexts. `req` is only present during SSR (the initial page load);
 * on client-side navigation (SPA link clicks) Next.js calls
 * getInitialProps again with `req` undefined, so we fall back to reading
 * `document.cookie` directly from the browser.
 */
export const getPreviewAccessCookie = (
  req: CustomNextPageContext['req'] | undefined,
): string | undefined => {
  if (req) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (req as any)?.cookies?.[COOKIE_NAME];
  }

  if (typeof document !== 'undefined') {
    return readCookieFromDocumentString(document.cookie, COOKIE_NAME);
  }

  return undefined;
};
